import { useCallback, useState } from 'react'
import { trackUserEvent } from '@vend/utilities'
import {
  useCustomerManager,
  useRegisterClosureManager,
  useRegisterManager,
  useSaleLoader,
  useSaleManager,
  useSyncManager,
  useTrainingModeManager,
  useUserSwitchingService,
} from '../../ng-compat/hooks'
import { $rootScope } from '../../ng-compat/imports'
import {
  getOutstandingBalance,
  isConfirmedNotReadyToComplete,
  isLayby,
  isOnAccount,
  isOpen,
  isParked,
  isReturn,
  isValidCustomer,
} from '../../functions/sale'
import { SaleStatus } from '../../models/sale.constants'
import { useAppDispatch } from '../../state/hooks'
import {
  updateCalculatingStateOfAllLineItems,
  updateInitialPaidBalance,
  writeMissingSaleId,
} from '../../state/slices/currentSale/currentSaleSlice'
import { WEB_REGISTER } from '../web-register.constant'
import { Sale } from '../../models/sale'
import { useSaleLoadErrors } from './useSaleLoadErrors'
import { useSaleModals } from './useSaleModals'

const focusSearch = () => {
  setTimeout(() => {
    $rootScope.$emit('autocomplete:global-search:focus')
  })
}

export const useLoadSale = () => {
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const syncManager = useSyncManager()
  const saleLoader = useSaleLoader()
  const registerManager = useRegisterManager()
  const registerClosureManager = useRegisterClosureManager()
  const customerManager = useCustomerManager()
  const trainingModeManager = useTrainingModeManager()
  const userSwitchingService = useUserSwitchingService()
  const {
    openRegisterModal,
    openOutletsDifferWarningModal,
    openAddCustomerModal,
  } = useSaleModals()
  const { handleLoadError } = useSaleLoadErrors()
  const [saleLoading, setSaleLoading] = useState(false)

  const loadSale = useCallback(
    async (saleId: string) => {
      let loadedSale: Sale | null = null
      try {
        await userSwitchingService.switchUserIfNeeded()
        await syncManager.sync({ skipEmitSyncComplete: true })
        loadedSale = await saleLoader.loadSale(saleId)

        if (!registerManager.isActiveRegisterOpen()) {
          if (!registerClosureManager.hasOpenClosePermission()) {
            throw new Error('no_register_open_permission')
          }
          loadedSale = await new Promise(resolve => {
            openRegisterModal(settings => {
              if (settings) {
                return registerClosureManager
                  .openRegister(registerManager.getActiveRegister(), settings)
                  .then(() => resolve(loadedSale))
              }
              return resolve(null)
            })
          })
        }

        if (
          loadedSale &&
          !isOpen(loadedSale.status) &&
          isValidCustomer(loadedSale.customer)
        ) {
          await customerManager.refreshCustomer(loadedSale.customer)
        }

        // Depending on how the sale was loaded, we may need to initialise the sale manager
        await saleManager.init()

        if (!loadedSale) {
          // Sale load cancelled due to closed register
          return
        }

        if (trainingModeManager.trainingModeEnabled) {
          await trainingModeManager.exitTrainingMode()
        }

        if (
          loadedSale.status === SaleStatus.LAYBY_SALE ||
          loadedSale.status === SaleStatus.ACCOUNT_SALE
        ) {
          const saleRegister = await registerManager.getRegisterById(
            loadedSale.registerId!
          )
          const activeOutlet = registerManager.getActiveOutlet()
          // If the outlet of the loaded sale's register does not match the active outlet, warn the user
          if (saleRegister && saleRegister.outlet_id !== activeOutlet.id) {
            const outlet = await registerManager.getOutletById(
              saleRegister.outlet_id
            )
            openOutletsDifferWarningModal({
              outletName: outlet!.name,
              status: loadedSale.status,
            })
          }
        }

        const {
          hasActiveSale,
        } = await saleManager.actionExistingSaleIfRequired({
          saleToLoad: saleId,
        })
        if (hasActiveSale) {
          focusSearch()
          return
        }

        // Ensure that we don't change the sync status of a loaded, on account or layby or unfulfilled
        // sale from "unmodified" to "modified" when setting the active sale.
        const saleModificationOptions = {
          unconfirmedSalesOnly:
            isConfirmedNotReadyToComplete({
              status: loadedSale.status as SaleStatus,
              readyToComplete: loadedSale.readyToComplete,
              isSubmittedSaleEarly: loadedSale.isSubmittedSaleEarly,
            }) || Boolean(loadedSale.isSavedUnfulfilled),
        }

        await saleManager.setActiveSale(loadedSale, saleModificationOptions)

        if (!isValidCustomer(loadedSale.customer) && isReturn(loadedSale)) {
          openAddCustomerModal()
        }

        if (loadedSale && isParked(loadedSale.status)) {
          await saleManager.saleCalculationQueue()
        }

        if (
          loadedSale &&
          (isLayby(loadedSale.status) || isOnAccount(loadedSale.status))
        ) {
          dispatch(updateInitialPaidBalance(getOutstandingBalance(loadedSale)))
        }
      } catch (error) {
        handleLoadError(loadedSale, error)
      }

      dispatch(updateCalculatingStateOfAllLineItems(false))
      focusSearch()

      return loadedSale
    },
    [
      customerManager,
      dispatch,
      handleLoadError,
      openAddCustomerModal,
      openOutletsDifferWarningModal,
      openRegisterModal,
      registerClosureManager,
      registerManager,
      saleLoader,
      saleManager,
      syncManager,
      trainingModeManager,
      userSwitchingService,
    ]
  )

  const retrieveParkedSale = useCallback(
    async (saleId: string) => {
      setSaleLoading(true)
      const result = await saleManager.actionExistingSaleIfRequired({
        saleToLoad: saleId,
      })
      if (result.hasActiveSale) {
        focusSearch()
        setSaleLoading(false)
        return null
      }

      const sale = await loadSale(saleId)

      if (sale && !sale.id) {
        // For trouble shooting: Missing sale id in retrieved sale and
        // trigger overwriting sale rejection when calling createSaleMutation
        trackUserEvent(
          WEB_REGISTER.APPLICATION_NAME,
          'Sale id missing in retrieved sale',
          {
            saleId: sale.localId,
          }
        )
        // Manually write sale.id back
        dispatch(writeMissingSaleId())
        setSaleLoading(false)
        return sale
      }

      setSaleLoading(false)
      return sale
    },
    [dispatch, loadSale, saleManager]
  )

  return { retrieveParkedSale, loadSale, saleLoading }
}
